import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const messages = [
  'Hey! How can I help you today?',
  'Looking for something specific? Let me find it!',
  'Did you see today\'s deals? They won\'t last long!',
  'Want me to show you the best shops nearby?',
  'Got a question? Ask me anything!',
  'Check out the trending products of the day!',
  'Where\'s your order? Let me track it for you.',
  'Shopping for a gift? I\'ll help you pick!',
  'Looking for great deals? Just ask!',
  'Hungry? Order food and get it delivered fast!',
  'Need a quick purchase? Tell me what you need.',
  'Want to see what other buyers are saying?',
  'Ready to find an amazing deal? Let\'s go!',
  'Any questions about products? I\'m right here.',
  'Got something in your cart that needs finishing up?',
];

interface AiTypingSubtitleProps {
  isDarkMode: boolean;
}

export default function AiTypingSubtitle({ isDarkMode }: AiTypingSubtitleProps) {
  const [displayText, setDisplayText] = useState('');
  const [messageIndex, setMessageIndex] = useState(0);
  const [phase, setPhase] = useState<'typing' | 'pausing' | 'deleting'>('typing');
  const [charIndex, setCharIndex] = useState(0);
  const lastIndexRef = useRef(-1);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getRandomIndex = useCallback(() => {
    let idx: number;
    do {
      idx = Math.floor(Math.random() * messages.length);
    } while (idx === lastIndexRef.current && messages.length > 1);
    lastIndexRef.current = idx;
    return idx;
  }, []);

  // Pick first message
  useEffect(() => {
    const idx = getRandomIndex();
    setMessageIndex(idx);
    setDisplayText('');
    setCharIndex(0);
    setPhase('typing');
  }, [getRandomIndex]);

  // Typing / pausing / deleting logic
  useEffect(() => {
    const currentMessage = messages[messageIndex];

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (phase === 'typing') {
      if (charIndex < currentMessage.length) {
        timerRef.current = setTimeout(() => {
          setDisplayText(currentMessage.slice(0, charIndex + 1));
          setCharIndex(charIndex + 1);
        }, 50 + Math.random() * 30); // slight randomness for natural feel
      } else {
        // Done typing, pause before deleting
        timerRef.current = setTimeout(() => {
          setPhase('pausing');
        }, 2500);
      }
    } else if (phase === 'pausing') {
      timerRef.current = setTimeout(() => {
        setPhase('deleting');
        setCharIndex(currentMessage.length);
      }, 2000);
    } else if (phase === 'deleting') {
      if (charIndex > 0) {
        timerRef.current = setTimeout(() => {
          const nextIdx = charIndex - 1;
          setDisplayText(currentMessage.slice(0, nextIdx));
          setCharIndex(nextIdx);
        }, 25 + Math.random() * 15);
      } else {
        // Done deleting, pick new message
        timerRef.current = setTimeout(() => {
          const newIdx = getRandomIndex();
          setMessageIndex(newIdx);
          setDisplayText('');
          setCharIndex(0);
          setPhase('typing');
        }, 400);
      }
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [phase, charIndex, messageIndex, getRandomIndex]);

  return (
    <div className="relative inline-flex items-center justify-center min-h-[18px]">
      <span className={`text-[10px] ${isDarkMode ? 'text-stone-400' : 'text-stone-500'}`}>
        {displayText}
      </span>
      {/* Blinking cursor */}
      <motion.span
        animate={{ opacity: [1, 0, 1] }}
        transition={{ duration: 0.8, repeat: Infinity, ease: 'easeInOut' }}
        className={`inline-block w-[1.5px] h-[12px] ml-0.5 rounded-full ${
          isDarkMode ? 'bg-gold/80' : 'bg-gold/80'
        }`}
      />
    </div>
  );
}